/**
 * Configurateur - Optimisations et Fonctionnalités Avancées
 * Module complémentaire pour améliorer les performances et ajouter des fonctionnalités avancées
 */

class ConfiguratorOptimizations {
  constructor(configurator) {
    this.configurator = configurator;
    this.container = configurator.container;
    this.config = configurator.config;
    this.lazyLoadingEnabled = true;
    this.analyticsEnabled = this.config.config?.settings?.enable_analytics || false;
    this.debounceTimers = {};
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.performanceMetrics = {
      initTime: 0,
      renderTime: 0,
      interactionCount: 0,
      loadTime: performance.now()
    };

    this.init();
  }

  init() {
    this.setupLazyLoading();
    this.setupPerformanceMonitoring();
    this.setupResponsiveHandling();
    this.setupAdvancedInteractions();
    this.setupAnalytics();
    this.setupAccessibility();
    this.setupOfflineSupport();
    
    // Log initialization
    console.info('Configurator optimizations initialized');
    this.performanceMetrics.initTime = performance.now() - this.performanceMetrics.loadTime;
  }

  // Lazy Loading d'images
  setupLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      this.lazyLoadingEnabled = false;
      return;
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const src = img.dataset.src;
          
          if (src) {
            // Préchargement de l'image
            const preloadImg = new Image();
            preloadImg.onload = () => {
              img.src = src;
              img.classList.add('loaded');
              img.removeAttribute('data-src');
            };
            preloadImg.src = src;
            
            // Arrêter d'observer cette image
            this.intersectionObserver.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '200px', // Charger les images 200px avant qu'elles ne soient visibles
      threshold: 0.1
    });

    // Observer toutes les images avec data-src
    this.container.querySelectorAll('img[data-src]').forEach(img => {
      this.intersectionObserver.observe(img);
    });
  }

  // Monitoring des performances
  setupPerformanceMonitoring() {
    // Mesurer le temps de rendu
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (window.performance && window.performance.getEntriesByType) {
          const paintMetrics = window.performance.getEntriesByType('paint');
          const firstPaint = paintMetrics.find(entry => entry.name === 'first-paint');
          const firstContentfulPaint = paintMetrics.find(entry => entry.name === 'first-contentful-paint');
          
          if (firstPaint) {
            this.performanceMetrics.firstPaint = firstPaint.startTime;
          }
          
          if (firstContentfulPaint) {
            this.performanceMetrics.firstContentfulPaint = firstContentfulPaint.startTime;
          }
        }
        
        this.performanceMetrics.renderTime = performance.now() - this.performanceMetrics.loadTime;
      }, 0);
    });

    // Surveiller les interactions utilisateur
    this.container.addEventListener('click', () => {
      this.performanceMetrics.interactionCount++;
    });

    // Rapport de performance après chargement complet
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.reportPerformance();
      }, 1000);
    });
  }

  reportPerformance() {
    console.info('Configurator Performance Metrics:', {
      initTime: Math.round(this.performanceMetrics.initTime) + 'ms',
      renderTime: Math.round(this.performanceMetrics.renderTime) + 'ms',
      firstPaint: this.performanceMetrics.firstPaint ? Math.round(this.performanceMetrics.firstPaint) + 'ms' : 'N/A',
      firstContentfulPaint: this.performanceMetrics.firstContentfulPaint ? Math.round(this.performanceMetrics.firstContentfulPaint) + 'ms' : 'N/A',
      totalLoadTime: Math.round(performance.now() - this.performanceMetrics.loadTime) + 'ms'
    });

    // Envoyer les métriques à l'analytics si activé
    if (this.analyticsEnabled && typeof gtag !== 'undefined') {
      gtag('event', 'configurator_performance', {
        'init_time': Math.round(this.performanceMetrics.initTime),
        'render_time': Math.round(this.performanceMetrics.renderTime),
        'first_paint': this.performanceMetrics.firstPaint ? Math.round(this.performanceMetrics.firstPaint) : null,
        'first_contentful_paint': this.performanceMetrics.firstContentfulPaint ? Math.round(this.performanceMetrics.firstContentfulPaint) : null,
        'total_load_time': Math.round(performance.now() - this.performanceMetrics.loadTime)
      });
    }
  }

  // Gestion responsive avancée
  setupResponsiveHandling() {
    if (!('ResizeObserver' in window)) return;

    this.resizeObserver = new ResizeObserver(this.debounce(() => {
      this.handleResponsiveLayout();
    }, 200));

    this.resizeObserver.observe(this.container);
    this.handleResponsiveLayout();
  }

  handleResponsiveLayout() {
    const width = this.container.clientWidth;
    const breakpoints = this.config.config?.settings?.responsive || {
      mobile_breakpoint: 768,
      tablet_breakpoint: 1024,
      desktop_breakpoint: 1200
    };

    // Appliquer les classes responsive
    this.container.classList.remove('mobile', 'tablet', 'desktop', 'large-desktop');
    
    if (width < breakpoints.mobile_breakpoint) {
      this.container.classList.add('mobile');
      this.optimizeForMobile();
    } else if (width < breakpoints.tablet_breakpoint) {
      this.container.classList.add('tablet');
      this.optimizeForTablet();
    } else if (width < breakpoints.desktop_breakpoint) {
      this.container.classList.add('desktop');
      this.optimizeForDesktop();
    } else {
      this.container.classList.add('large-desktop');
      this.optimizeForLargeDesktop();
    }
  }

  optimizeForMobile() {
    // Simplifier l'interface pour mobile
    const productGrids = this.container.querySelectorAll('.product-grid');
    productGrids.forEach(grid => {
      grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(140px, 1fr))';
    });

    // Réduire la taille des images
    const productImages = this.container.querySelectorAll('.product-image');
    productImages.forEach(img => {
      if (img.dataset.mobileSize) {
        img.dataset.originalSize = img.src;
        img.src = img.dataset.mobileSize;
      }
    });

    // Simplifier la navigation
    const navigation = this.container.querySelector('.configurator-navigation');
    if (navigation) {
      navigation.classList.add('simplified');
    }
  }

  optimizeForTablet() {
    // Restaurer les images originales si elles ont été changées
    const productImages = this.container.querySelectorAll('.product-image');
    productImages.forEach(img => {
      if (img.dataset.originalSize) {
        img.src = img.dataset.originalSize;
      }
    });

    // Ajuster la disposition pour tablette
    const productGrids = this.container.querySelectorAll('.product-grid');
    productGrids.forEach(grid => {
      grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(180px, 1fr))';
    });
  }

  optimizeForDesktop() {
    // Restaurer les images originales
    const productImages = this.container.querySelectorAll('.product-image');
    productImages.forEach(img => {
      if (img.dataset.originalSize) {
        img.src = img.dataset.originalSize;
      }
    });

    // Optimiser pour desktop
    const productGrids = this.container.querySelectorAll('.product-grid');
    productGrids.forEach(grid => {
      grid.style.gridTemplateColumns = '';
    });

    // Activer les fonctionnalités avancées
    this.container.classList.add('advanced-features');
  }

  optimizeForLargeDesktop() {
    this.optimizeForDesktop();
    
    // Ajouter des améliorations pour grands écrans
    this.container.classList.add('enhanced-visuals');
    
    // Activer les animations avancées si supportées
    if (this.config.config?.settings?.animations?.enabled !== false) {
      this.container.classList.add('advanced-animations');
    }
  }

  // Interactions avancées
  setupAdvancedInteractions() {
    this.setupTooltips();
    this.setupDragAndDrop();
    this.setupKeyboardNavigation();
    this.setupGestures();
    this.setupParticleEffects();
  }

  setupTooltips() {
    // Créer des tooltips pour les éléments avec data-tooltip
    this.container.querySelectorAll('[data-tooltip]').forEach(element => {
      element.addEventListener('mouseenter', (e) => {
        const tooltip = document.createElement('div');
        tooltip.className = 'tooltip';
        tooltip.textContent = element.dataset.tooltip;
        
        document.body.appendChild(tooltip);
        
        const rect = element.getBoundingClientRect();
        tooltip.style.left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
        tooltip.style.top = rect.top - tooltip.offsetHeight - 10 + 'px';
        
        tooltip.classList.add('show');
        
        element.addEventListener('mouseleave', () => {
          tooltip.classList.remove('show');
          setTimeout(() => {
            tooltip.remove();
          }, 300);
        });
      });
    });
  }

  setupDragAndDrop() {
    // Implémentation de drag & drop pour réorganiser les sélections
    if (!this.container.querySelector('.draggable-container')) return;
    
    const draggables = this.container.querySelectorAll('.draggable');
    const containers = this.container.querySelectorAll('.draggable-container');
    
    draggables.forEach(draggable => {
      draggable.addEventListener('dragstart', () => {
        draggable.classList.add('dragging');
      });
      
      draggable.addEventListener('dragend', () => {
        draggable.classList.remove('dragging');
        this.configurator.updateConfiguration();
      });
    });
    
    containers.forEach(container => {
      container.addEventListener('dragover', e => {
        e.preventDefault();
        const afterElement = this.getDragAfterElement(container, e.clientY);
        const draggable = this.container.querySelector('.dragging');
        if (afterElement == null) {
          container.appendChild(draggable);
        } else {
          container.insertBefore(draggable, afterElement);
        }
      });
    });
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.draggable:not(.dragging)')];
    
    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child };
      } else {
        return closest;
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element;
  }

  setupKeyboardNavigation() {
    // Améliorer la navigation clavier pour l'accessibilité
    this.container.querySelectorAll('.product-option, .color-option, .btn').forEach(element => {
      if (!element.getAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }
      
      element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          element.click();
        }
      });
    });
    
    // Navigation entre les étapes avec les touches
    document.addEventListener('keydown', (e) => {
      if (this.container.contains(document.activeElement) || this.container.matches(':focus-within')) {
        if (e.ctrlKey && e.key === 'ArrowRight') {
          e.preventDefault();
          const nextButton = this.container.querySelector('.configurator-step.active [data-action="next"]');
          if (nextButton && !nextButton.disabled) {
            nextButton.click();
          }
        } else if (e.ctrlKey && e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevButton = this.container.querySelector('.configurator-step.active [data-action="previous"]');
          if (prevButton) {
            prevButton.click();
          }
        }
      }
    });
  }

  setupGestures() {
    // Support des gestes tactiles pour la navigation
    if (!('ontouchstart' in window)) return;
    
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.container.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.container.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    this.handleSwipe = () => {
      const minSwipeDistance = 100;
      const swipeDistance = touchEndX - touchStartX;
      
      if (Math.abs(swipeDistance) < minSwipeDistance) return;
      
      if (swipeDistance > 0) {
        // Swipe droite (précédent)
        const prevButton = this.container.querySelector('.configurator-step.active [data-action="previous"]');
        if (prevButton) {
          prevButton.click();
        }
      } else {
        // Swipe gauche (suivant)
        const nextButton = this.container.querySelector('.configurator-step.active [data-action="next"]');
        if (nextButton && !nextButton.disabled) {
          nextButton.click();
        }
      }
    };
  }

  setupParticleEffects() {
    // Effets de particules pour les sélections
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('.product-option') || e.target.closest('.color-option')) {
        this.createParticles(e.clientX, e.clientY);
      }
    });
  }

  createParticles(x, y) {
    const particleCount = 8;
    const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545'];
    
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'particle-effect';
      particle.style.left = x + 'px';
      particle.style.top = y + 'px';
      particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      particle.style.transform = `translateY(0) scale(1) rotate(${Math.random() * 360}deg)`;
      
      document.body.appendChild(particle);
      
      setTimeout(() => {
        particle.remove();
      }, 1000);
    }
  }

  // Analytics et tracking
  setupAnalytics() {
    if (!this.analyticsEnabled) return;
    
    // Tracking des étapes
    this.configurator.subscribe((event, data) => {
      if (event === 'step_changed' && typeof gtag !== 'undefined') {
        gtag('event', 'configurator_step_changed', {
          'step_id': data.stepId,
          'step_index': data.stepIndex,
          'direction': data.direction
        });
      }
      
      if (event === 'configuration_updated' && typeof gtag !== 'undefined') {
        gtag('event', 'configurator_selection_made', {
          'step_id': data.stepId,
          'option': data.option,
          'value_type': typeof data.value
        });
      }
      
      if (event === 'pricing_updated' && typeof gtag !== 'undefined') {
        gtag('event', 'configurator_price_updated', {
          'total_price': data.totalPrice,
          'base_price': data.basePrice,
          'supplements_count': Object.keys(data.supplements).length
        });
      }
      
      if (event === 'add_to_cart' && typeof gtag !== 'undefined') {
        gtag('event', 'configurator_add_to_cart', {
          'product_id': this.config.productId,
          'price': data.price,
          'quantity': data.quantity,
          'configuration': JSON.stringify(data.configuration)
        });
      }
    });
    
    // Tracking du temps passé sur chaque étape
    let stepEnterTime = Date.now();
    let currentStepIndex = this.configurator.currentStep;
    
    this.configurator.subscribe((event, data) => {
      if (event === 'step_changed') {
        const timeSpent = Date.now() - stepEnterTime;
        
        if (typeof gtag !== 'undefined') {
          gtag('event', 'configurator_step_time', {
            'step_index': currentStepIndex,
            'time_spent_ms': timeSpent
          });
        }
        
        currentStepIndex = data.stepIndex;
        stepEnterTime = Date.now();
      }
    });
  }

  // Accessibilité
  setupAccessibility() {
    // Améliorer l'accessibilité ARIA
    this.container.querySelectorAll('.configurator-step').forEach((step, index) => {
      step.setAttribute('role', 'tabpanel');
      step.setAttribute('aria-labelledby', `step-tab-${index}`);
      step.setAttribute('id', `step-panel-${index}`);
      
      if (!step.classList.contains('active')) {
        step.setAttribute('aria-hidden', 'true');
      } else {
        step.setAttribute('aria-hidden', 'false');
      }
    });
    
    // Créer une navigation ARIA
    const stepsNav = document.createElement('div');
    stepsNav.className = 'steps-nav sr-only';
    stepsNav.setAttribute('role', 'tablist');
    stepsNav.setAttribute('aria-label', 'Étapes du configurateur');
    
    this.config.config.steps.forEach((step, index) => {
      const stepTab = document.createElement('button');
      stepTab.id = `step-tab-${index}`;
      stepTab.className = 'step-tab';
      stepTab.setAttribute('role', 'tab');
      stepTab.setAttribute('aria-controls', `step-panel-${index}`);
      stepTab.setAttribute('aria-selected', index === this.configurator.currentStep ? 'true' : 'false');
      stepTab.textContent = step.title;
      
      stepTab.addEventListener('click', () => {
        this.configurator.goToStep(index);
      });
      
      stepsNav.appendChild(stepTab);
    });
    
    this.container.insertBefore(stepsNav, this.container.firstChild);
    
    // Mettre à jour les attributs ARIA lors des changements d'étape
    this.configurator.subscribe((event, data) => {
      if (event === 'step_changed') {
        this.container.querySelectorAll('.step-tab').forEach((tab, index) => {
          tab.setAttribute('aria-selected', index === data.stepIndex ? 'true' : 'false');
        });
        
        this.container.querySelectorAll('.configurator-step').forEach((panel, index) => {
          panel.setAttribute('aria-hidden', index === data.stepIndex ? 'false' : 'true');
        });
      }
    });
    
    // Ajouter des descriptions pour les lecteurs d'écran
    this.container.querySelectorAll('[data-action]').forEach(element => {
      if (!element.getAttribute('aria-label')) {
        const action = element.dataset.action;
        let label = '';
        
        switch (action) {
          case 'next':
            label = 'Passer à l\'étape suivante';
            break;
          case 'previous':
            label = 'Revenir à l\'étape précédente';
            break;
          case 'add-to-cart':
            label = 'Ajouter au panier';
            break;
          case 'select-product':
            const productTitle = element.closest('.product-option')?.querySelector('.product-title')?.textContent;
            label = `Sélectionner ${productTitle || 'ce produit'}`;
            break;
        }
        
        if (label) {
          element.setAttribute('aria-label', label);
        }
      }
    });
  }

  // Support hors ligne
  setupOfflineSupport() {
    // Sauvegarder la configuration localement
    const saveInterval = this.config.config?.settings?.auto_save_interval || 5000;
    
    setInterval(() => {
      this.saveConfigurationLocally();
    }, saveInterval);
    
    // Détecter les changements de connectivité
    window.addEventListener('online', () => {
      this.container.classList.remove('offline-mode');
      this.showMessage('Connexion rétablie. Vos modifications ont été sauvegardées.', 'success');
      this.syncOfflineChanges();
    });
    
    window.addEventListener('offline', () => {
      this.container.classList.add('offline-mode');
      this.showMessage('Vous êtes hors ligne. Vos modifications seront sauvegardées localement.', 'warning');
    });
    
    // Charger la configuration sauvegardée au démarrage
    this.loadSavedConfiguration();
  }

  saveConfigurationLocally() {
    try {
      const configData = {
        productId: this.config.productId,
        configuration: this.configurator.getConfiguration(),
        pricing: this.configurator.getPricing(),
        currentStep: this.configurator.getCurrentStep(),
        timestamp: Date.now()
      };
      
      localStorage.setItem(`configurator_${this.config.productId}`, JSON.stringify(configData));
      
      // Stocker également dans IndexedDB pour une persistance plus robuste
      if ('indexedDB' in window) {
        this.storeInIndexedDB(configData);
      }
    } catch (error) {
      console.error('Error saving configuration locally:', error);
    }
  }

  storeInIndexedDB(configData) {
    const request = indexedDB.open('ConfiguratorDB', 1);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('configurations')) {
        db.createObjectStore('configurations', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      const transaction = db.transaction(['configurations'], 'readwrite');
      const store = transaction.objectStore('configurations');
      
      configData.id = `product_${this.config.productId}`;
      store.put(configData);
    };
  }

  loadSavedConfiguration() {
    try {
      // Essayer d'abord localStorage
      const savedData = localStorage.getItem(`configurator_${this.config.productId}`);
      
      if (savedData) {
        const configData = JSON.parse(savedData);
        
        // Vérifier si la configuration n'est pas trop ancienne (24h max)
        const maxAge = 24 * 60 * 60 * 1000; // 24 heures en ms
        if (Date.now() - configData.timestamp < maxAge) {
          this.restoreConfiguration(configData);
          return;
        }
      }
      
      // Si pas dans localStorage ou trop ancien, essayer IndexedDB
      if ('indexedDB' in window) {
        this.loadFromIndexedDB();
      }
    } catch (error) {
      console.error('Error loading saved configuration:', error);
    }
  }

  loadFromIndexedDB() {
    const request = indexedDB.open('ConfiguratorDB', 1);
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      
      if (db.objectStoreNames.contains('configurations')) {
        const transaction = db.transaction(['configurations'], 'readonly');
        const store = transaction.objectStore('configurations');
        const getRequest = store.get(`product_${this.config.productId}`);
        
        getRequest.onsuccess = () => {
          if (getRequest.result) {
            this.restoreConfiguration(getRequest.result);
          }
        };
      }
    };
  }

  restoreConfiguration(configData) {
    // Restaurer la configuration sauvegardée
    if (configData.configuration) {
      Object.keys(configData.configuration).forEach(stepId => {
        Object.keys(configData.configuration[stepId]).forEach(option => {
          this.configurator.updateConfiguration(
            stepId,
            option,
            configData.configuration[stepId][option]
          );
        });
      });
    }
    
    // Restaurer l'étape actuelle
    if (typeof configData.currentStep === 'number') {
      this.configurator.goToStep(configData.currentStep);
    }
    
    // Mettre à jour l'interface
    this.configurator.updatePricing();
    this.configurator.updateNavigation();
    this.configurator.updateStepValidation();
    
    // Notifier l'utilisateur
    this.showMessage('Configuration précédente restaurée', 'info');
  }

  syncOfflineChanges() {
    // Synchroniser les modifications faites hors ligne
    // Cette fonction serait implémentée selon les besoins spécifiques
  }

  showMessage(message, type = 'info') {
    this.configurator.showMessage(message, type);
  }

  // Utilitaires
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimers[func]);
      this.debounceTimers[func] = setTimeout(() => {
        func.apply(this, args);
      }, wait);
    };
  }

  // Nettoyage lors de la destruction
  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    // Supprimer les écouteurs d'événements
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    
    console.info('Configurator optimizations destroyed');
  }
}

// Initialisation automatique
document.addEventListener('DOMContentLoaded', function() {
  // Attendre que le configurateur principal soit initialisé
  const checkConfigurator = setInterval(() => {
    if (window.productConfigurator) {
      clearInterval(checkConfigurator);
      window.configuratorOptimizations = new ConfiguratorOptimizations(window.productConfigurator);
    }
  }, 100);
  
  // Timeout de sécurité
  setTimeout(() => {
    clearInterval(checkConfigurator);
  }, 5000);
});

// Export pour utilisation en module
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ConfiguratorOptimizations;
}

